import React, { useEffect, forwardRef } from "react";
import { useFormikContext } from "formik";
import { observer } from "mobx-react";
import { toast } from "react-toastify";
import { CHECK_LENGTH_PHONE_SEARCH } from "app/appConfig";
import SearchPhoneAndSelectInput from "app/PatientManagement/CheckInList/Component/SearchPhoneAndSelectInput";
import { renderOptionsCheckInCustomer } from "app/EgretLayout/SharedCompoents/SelectCustomer";
import { filterCustomerOptions } from "utils";

export const CompanionInitializationEffect = observer(() => {
  const { setFieldValue, values } = useFormikContext();

  useEffect(() => {
    if (values?.companion || values?.leadCompanion) {
      if (!values?.companionSearchPhone) {
        setFieldValue(
          "companionSearchPhone",
          values?.companion?.phoneNumber || values?.leadCompanion?.phoneNumber || ""
        );
      }
      if (!values?.isCompanion) {
        setFieldValue("isCompanion", true);
      }
    }
  }, [values?.companion, values?.leadCompanion]);

  useEffect(() => {
    if (values?.leadId) {
      if (values?.isCompanion || values?.companion || values?.leadCompanion || values?.companionSharedSource) {
        setFieldValue("isCompanion", false);
        setFieldValue("companion", null);
        setFieldValue("leadCompanion", null);
        setFieldValue("companionSearchPhone", "");
        setFieldValue("companionSourceId", "");
        setFieldValue("companionSharedSource", null);
      }
    }
  }, [values?.leadId]);

  return null;
});

export const CompanionAutocomplete = observer(() => {
  const { setFieldValue, values } = useFormikContext();

  return (
    <>
    <SearchPhoneAndSelectInput
      name={"companionSearchPhone"}
      label="Chọn SĐT người đi cùng"
      autoUpercase
      checkLength={CHECK_LENGTH_PHONE_SEARCH || 9}
      isSearchFreeSolo
      enableFallbackSearchByPhoneNumber
      onChange={(_, companion) => {
        if (companion) {
          const isLead = typeof companion === "object" && !!companion.saleType;
          let isSame = false;
          let selectedPhone = "";

          if (typeof companion === "object") {
            selectedPhone = companion.phoneNumber;
            if (isLead) {
              isSame = (companion.client?.emrMappingId && companion.client?.emrMappingId === values?.patient?.id) ||
                       (companion.client?.id && companion.client?.id === values?.patient?.crmUuid);
            } else {
              isSame = (companion.id && companion.id === values?.patient?.id) ||
                       (companion.crmUuid && companion.crmUuid === values?.patient?.crmUuid);
            }
          } else if (typeof companion === "string") {
            selectedPhone = companion;
          }

          const primaryPhone = values?.patient?.phoneNumber || values?.phoneNumber || "";
          const isSamePhone = selectedPhone && primaryPhone &&
                             selectedPhone.trim() === primaryPhone.trim();

          if (isSame || isSamePhone) {
            toast.error("Không được chọn người đi cùng trùng Số điện thoại/Thông tin khách hàng check in!");
            setFieldValue("leadCompanion", null);
            setFieldValue("companion", null);
            setFieldValue("companionSourceId", "");
            setFieldValue("companionSearchPhone", "");
            return;
          }

          if (typeof companion === "object") {
            if (isLead) {
              setFieldValue("leadCompanion", companion);
              setFieldValue("companion", null);
              const companionSource = companion.leadSource;
              setFieldValue("companionSourceId", companionSource?.id || "");
              if (companionSource?.sharedSource) {
                setFieldValue("currentPatientSource", companionSource.sharedSource);
                setFieldValue("currentPatientSourceDetail", null);
                setFieldValue("companionSharedSource", companionSource.sharedSource);
              } else {
                setFieldValue("companionSharedSource", null);
              }
            } else {
              setFieldValue("companion", companion);
              setFieldValue("leadCompanion", null);
              setFieldValue("companionSourceId", companion?.currentPatientSource?.id || "");
              if (companion?.currentPatientSource?.sharedSource) {
                setFieldValue("currentPatientSource", companion.currentPatientSource.sharedSource);
                setFieldValue("currentPatientSourceDetail", null);
                setFieldValue("companionSharedSource", companion.currentPatientSource.sharedSource);
              } else {
                setFieldValue("companionSharedSource", null);
              }
            }
          }
        } else {
          setFieldValue("leadCompanion", null);
          setFieldValue("companion", null);
          setFieldValue("companionSourceId", "");
          setFieldValue("currentPatientSource", null);
          setFieldValue("currentPatientSourceDetail", null);
        }
      }}
      getOptionDisabled={(option) => {
        if (!option) return false;
        const isSamePhone = option?.phoneNumber && values?.patient?.phoneNumber &&
                           option.phoneNumber.trim() === values.patient.phoneNumber.trim();
        
        if (isSamePhone) return true;

        if (option.saleType) {
          return (option.client?.emrMappingId && option.client?.emrMappingId === values?.patient?.id) ||
                 (option.client?.id && option.client?.id === values?.patient?.crmUuid);
        }
        return (option.id && option.id === values?.patient?.id) ||
               (option.crmUuid && option.crmUuid === values?.patient?.crmUuid);
      }}
      disableClearable
      ListboxComponent={forwardRef((props, ref) => {
        const { children, ...other } = props;
        return (
          <ul {...other} ref={ref}>
            <ol className={"custom-listbox-customer box-checkin"}>
              <li>{children}</li>
            </ol>
          </ul>
        );
      })}
      renderOption={(option) =>
        renderOptionsCheckInCustomer({
          ...option,
          currentPatientSourceName:
            option?.currentPatientSource?.name || option?.currentPatientSourceName,
        })
      }
      filterOptions={filterCustomerOptions}
      handleOnInput={(text) => {
        if (!text) {
          setFieldValue("leadCompanion", null);
          setFieldValue("companion", null);
          setFieldValue("companionSourceId", "");
        }
      }}
    />
    {(values?.leadCompanion || values?.companion) && (
      <div className="mt-2 text-primary">
          <b>Người đi cùng:</b>{" "}
          {values?.companion ? (
              `Đã chọn KH (${values?.companion?.code || ""} ${values?.companion?.displayName || values?.companion?.fullName || ""})${
                values?.companion?.currentPatientSource
                  ? ` - Nguồn: ${values?.companion?.currentPatientSource?.name || ""}`
                  : ""
              }`
          ) : (
              `Đã chọn Lead (${values?.leadCompanion?.code || ""} ${values?.leadCompanion?.displayName || values?.leadCompanion?.fullName || ""}) ${values?.leadCompanion?.leadSource ? ` - Nguồn: ${values?.leadCompanion?.leadSource?.name || ""}` : ""} ${values?.leadCompanion?.agent ? ` - Telesale: ${values?.leadCompanion?.agent?.staffName || values?.leadCompanion?.agent?.name || ""}` : ""}`
          )}
      </div>
    )}
  </>
  );
});
